import { useEffect, useState } from "react";
import { askNotify, notifyState } from "./notify";
import { isDesktop } from "./desktop";
import { go, parseUrl, useStore } from "./store";
import { ChatView } from "./components/ChatView";
import { Members, NewGroup, NewMessage, People } from "./components/Dialogs";
import { Tray, ThreadPanel } from "./components/Rail";
import { Sidebar } from "./components/Sidebar";
import { TaskPage, TasksPage } from "./components/Tasks";
import { LaterPage, MentionsPage, SearchPage, ThreadsPage, UnreadsPage } from "./components/Pages";
import { isOwner, Toaster } from "./components/ui";
import { Guard } from "./components/Guard";

type Dialog = null | "new" | "group" | "people" | { members: number };

export function App() {
  const phase = useStore((s) => s.phase);
  if (phase === "loading") return null;
  if (phase === "signed-out") return <SignIn />;
  return <Shell />;
}

function Shell() {
  const route = useStore((s) => s.route);
  const rooms = useStore((s) => s.rooms);
  const me = useStore((s) => s.me!);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [perm, setPerm] = useState(notifyState());

  useEffect(() => {
    const pop = () => useStore.setState({ route: parseUrl(location.pathname) });
    addEventListener("popstate", pop);
    const key = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setDialog("new"); return; }
      // ⌥↑/⌥↓ next chat; ⌥⇧↑/⌥⇧↓ next chat with something unread (Slack's keys).
      if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) { e.preventDefault(); stepChat(e.key === "ArrowUp" ? -1 : 1, e.shiftKey); return; }
      if (e.key === "Escape") { const r = useStore.getState().route; if (r.v === "room" && r.thread && !document.querySelector(".veil,.menu,.picker,.pop,.prof")) go({ v: "room", id: r.id }); }
    };
    addEventListener("keydown", key);
    return () => { removeEventListener("popstate", pop); removeEventListener("keydown", key); };
  }, []);

  // Home, or a chat that's gone: open the first chat there is.
  useEffect(() => {
    if (route.v === "home" || (route.v === "room" && !rooms[route.id] && Object.keys(rooms).length)) {
      const first = Object.values(rooms).sort((a, b) => (a.kind === b.kind ? (a.name ?? "").localeCompare(b.name ?? "") : a.kind === "group" ? -1 : 1))[0];
      go(first ? { v: "room", id: first.id } : { v: "tasks", tab: isOwner(me) ? "everyone" : "mine" }, true);
    }
  }, [route, rooms, me]);

  const wide = route.v === "tasks";
  useEffect(() => { document.body.classList.toggle("wide", wide); }, [wide]);

  return (
    <>
      <Sidebar onNew={() => setDialog("new")} onNewGroup={() => setDialog("group")} onPeople={() => setDialog("people")} />
      <main className="chat">
        {perm === "default" && !isDesktop() && (
          <div className="col" style={{ flex: "none" }}>
            <div className="banner">Turn on notifications so new messages reach you, like Slack.
              <button onClick={async () => { await askNotify(); setPerm(notifyState()); }}>Turn on</button></div>
          </div>
        )}
        <Guard key={`${route.v}-${"id" in route ? route.id : ""}`} label="this page">
          {route.v === "room" && rooms[route.id] && <ChatView key={route.id} roomId={route.id} focus={route.focus} onMembers={() => setDialog({ members: route.id })} />}
          {route.v === "tasks" && <TasksPage tab={route.tab} />}
          {route.v === "task" && <TaskPage key={route.id} id={route.id} />}
          {route.v === "search" && <SearchPage q={route.q} />}
          {route.v === "mentions" && <MentionsPage />}
          {route.v === "threads" && <ThreadsPage />}
          {route.v === "later" && <LaterPage />}
          {route.v === "unreads" && <UnreadsPage />}
        </Guard>
      </main>
      <Guard key={`r-${route.v}-${"id" in route ? route.id : ""}-${route.v === "room" ? route.thread ?? "" : ""}`} label="the side panel">
        {route.v === "room" && route.thread ? <ThreadPanel key={route.thread} roomId={route.id} rootId={route.thread} />
          : !wide && <Tray roomId={route.v === "room" ? route.id : null} />}
      </Guard>
      {dialog === "new" && <NewMessage onClose={() => setDialog(null)} />}
      {dialog === "group" && <NewGroup onClose={() => setDialog(null)} />}
      {dialog === "people" && <People onClose={() => setDialog(null)} />}
      {dialog && typeof dialog === "object" && <Members roomId={dialog.members} onClose={() => setDialog(null)} />}
      <Toaster />
    </>
  );
}

/** The chats in left-bar order: groups, then direct messages. */
function stepChat(dir: 1 | -1, unreadOnly: boolean) {
  const s = useStore.getState(), me = s.me!.id;
  const name = (r: (typeof s.rooms)[number]) => r.kind === "group" ? r.name ?? "" : r.members.filter((u) => u !== me).map((u) => s.users[u]?.name).join(", ");
  const list = Object.values(s.rooms).sort((a, b) => (a.kind === b.kind ? name(a).localeCompare(name(b)) : a.kind === "group" ? -1 : 1));
  const cur = s.route.v === "room" ? list.findIndex((r) => r.id === (s.route as { id: number }).id) : -1;
  for (let i = 1; i <= list.length; i++) {
    const r = list[(cur + dir * i + list.length * 2) % list.length];
    if (!unreadOnly || (s.states[r.id]?.unread ?? 0) > 0) return go({ v: "room", id: r.id });
  }
}

function SignIn() {
  const q = new URLSearchParams(location.search).get("login");
  const dev = import.meta.env.DEV;
  return (
    <div className="login">
      <div className="in">
        <div className="logo"><span className="mk">R</span>Rockenue</div>
        <a className="gbtn" href="/auth/google">Sign in with Google</a>
        {q === "not-allowed" && <p>That account isn't on the team. Ask Karol to add you.</p>}
        {q === "failed" && <p>Sign-in didn't work. Try again.</p>}
        {dev && <div className="dev">{["karol", "office", "ruslana"].map((u) => <a key={u} href={`/auth/dev?email=${u}@rockenue.com`}>dev: {u}</a>)}</div>}
      </div>
    </div>
  );
}
